import {
  getActivitiesCollectionRef,
  getBanListRef,
  getHistoriesRef,
  getPackagesCollectionRef,
  getPropertyCollectionRef,
  getPropertyRef,
  getRolesCollectionRef,
  getSessionRef,
  getUserCollectionRef,
  getUserRef,
  getVisitorRef,
} from "./collectionReferences";
import {
  addDoc,
  deleteDoc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  onSnapshot,
  where,
  limit,
  startAfter,
  getCountFromServer,
} from "firebase/firestore";
import {
  chat_status,
  MESSAGE_TYPES,
  SUPER_ADMIN_SLUG,
} from "../utils/constants";

export async function getRoles() {
  const rolesRef = getRolesCollectionRef();
  const roles = await getDocs(
    query(rolesRef, where("slug", "!=", SUPER_ADMIN_SLUG))
  );

  return roles.docs.map((row) => ({ ...row.data() }));
}

export async function updateUser(data, id) {
  const userRef = getUserRef(id);
  await updateDoc(userRef, data);
  return data;
}

export async function getRoleBySlug(slug) {
  const rolesRef = getRolesCollectionRef();
  const roles = await getDocs(query(rolesRef, where("slug", "==", slug)));

  return roles.docs.map((row) => ({ ...row.data() })).pop();
}

export async function getPropertiesByCompanyId(companyId) {
  const propertyRef = getPropertyCollectionRef();
  const propertyQuery = query(
    propertyRef,
    where("companyId", "==", companyId),
    orderBy("createdAt", "asc")
  );
  const properties = (await getDocs(propertyQuery)).docs;

  return properties.map((row) => ({ id: row.id, ...row.data() }));
}

export async function getAssignedProperties(
  companyId,
  assigned_property_ids = []
) {
  if (assigned_property_ids.length > 0) {
    // Make chunks of assigned_property_ids
    const chunkSize = 10;
    const chunks = [];
    for (let i = 0; i < assigned_property_ids.length; i += chunkSize) {
      chunks.push(assigned_property_ids.slice(i, i + chunkSize));
    }
    const promises = chunks?.map(async (chunk) => {
      const propertyRef = getPropertyCollectionRef();
      const propertyQuery = query(
        propertyRef,
        where("id", "in", chunk)
        // where("companyId", "==", companyId),
        // orderBy("createdAt", "asc")
      );
      return (await getDocs(propertyQuery)).docs.map((row) => ({
        id: row.id,
        ...row.data(),
      }));
    });

    // return all history

    return Promise.all(promises).then((results) => {
      return results.flat();
    });
  }
  return [];
}

export async function getPropertyById(propertyId) {
  const propertyRef = getPropertyRef(propertyId);
  const property = await getDoc(propertyRef);

  return { id: property.id, ...property.data() };
}

export async function getVisitor(visitorId) {
  const visitorRef = getVisitorRef(visitorId);
  const visitorSnap = await getDoc(visitorRef);
  return {
    id: visitorSnap.id,
    ...visitorSnap.data(),
  };
}

export function getVisitorQuery(visitorId, callback) {
  const visitorRef = getVisitorRef(visitorId);

  const unsubscribe = onSnapshot(visitorRef, (doc) => {
    // Check if the document exists
    if (doc.exists()) {
      // Document exists - it's either a modification or an initial fetch
      const visitorData = {
        id: doc.id,
        ...doc.data(),
      };
      callback({
        type: "modified",
        data: visitorData,
      });
    } else {
      // Document doesn't exist - it's removed
      callback({
        type: "removed",
      });
    }
  });

  // Return the unsubscribe function so you can stop listening to changes when needed
  return unsubscribe;
}

export async function getHistory(historyId) {
  const historyRef = getHistoriesRef(historyId);
  const historySnap = await getDoc(historyRef);
  return {
    id: historySnap.id,
    ...historySnap.data(),
  };
}

export async function getActivitiesBySession(visitorId, sessionId) {
  const visitorRef = getVisitorRef(visitorId);
  const sessionRef = getSessionRef(visitorRef, sessionId);
  const activitiesRef = getActivitiesCollectionRef(sessionRef);
  const activitiesQuery = query(activitiesRef, orderBy("created_at", "desc"));

  return (await getDocs(activitiesQuery)).docs.map((row) => ({
    id: row.id,
    ...row.data(),
  }));
}

export async function getSession(visitorId, sessionId) {
  const visitorRef = getVisitorRef(visitorId);
  const sessionRef = getSessionRef(visitorRef, sessionId);
  const sessionSnap = await getDoc(sessionRef);
  return {
    id: sessionSnap.id,
    ...sessionSnap.data(),
  };
}

export async function updateVisitor(visitorId, data, historyId = null) {
  const visitorRef = getVisitorRef(visitorId);
  await updateDoc(visitorRef, data);
  if (historyId) {
    const historyRef = getHistoriesRef(historyId);
    await setDoc(historyRef, { name: data.name }, { merge: true });
  }
}

export async function updateSession(visitorId, sessionId, data) {
  const visitorRef = getVisitorRef(visitorId);
  const sessionRef = getSessionRef(visitorRef, sessionId);
  await updateDoc(sessionRef, data);
}

export async function getUserById(userId) {
  const userRef = getUserRef(userId);
  return (await getDoc(userRef)).data();
}

export const checkPropertyExist = async (name, companyId) => {
  const propertyRef = getPropertyCollectionRef();
  const propertyQuery = query(
    propertyRef,
    where("name", ">=", name),
    where("name", "<=", name + "~"),
    where("companyId", "==", companyId)
  );

  return (await getDocs(propertyQuery)).docs.length > 0;
};

export const banVisitor = async (visitorId, session, data) => {
  const banListRef = getBanListRef(visitorId);
  const visitorRef = getVisitorRef(visitorId);
  const sessionRef = getSessionRef(visitorRef, session);
  const activitiesRef = getActivitiesCollectionRef(sessionRef);
  const activityObj = {
    body: `${data.name} banned by ${data.bannedByName}`,
    type: MESSAGE_TYPES.log,
    name: data.bannedByName,
    created_at: serverTimestamp(),
  };
  await setDoc(banListRef, data, { merge: true });
  await updateDoc(visitorRef, {
    chatStatus: chat_status.no_action,
    isNewMessage: false,
    isBanned: true,
  });
  await addDoc(activitiesRef, activityObj);
  return data;
};

export const unbanVisitor = async (visitorId) => {
  const banListRef = getBanListRef(visitorId);
  const visitorRef = getVisitorRef(visitorId);
  await deleteDoc(banListRef);
  await updateDoc(visitorRef, { isBanned: false });
  return visitorId;
};

// Get All Properties
export async function getAllProperties() {
  const propertiesRef = getPropertyCollectionRef();
  const snapshot = await getDocs(propertiesRef);
  const properties = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  return properties;
}

// Get All Users without Pagination
export async function getAllUsers() {
  const usersCollRef = getUserCollectionRef();

  const snapshot = await getDocs(usersCollRef);
  const users = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return users;
}

// Get All Packages
export async function getAllPackages() {
  const packagesRef = getPackagesCollectionRef();
  const snapshot = await getDocs(packagesRef);
  const packages = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  return packages;
}

export async function getTotalUsersCount() {
  const usersCollRef = getUserCollectionRef();
  const snapshot = await getCountFromServer(usersCollRef);
  return snapshot.data().count;
}
